import type { SwappedInboundMessage } from '@funkit/connect-core'
import { useCallback, useState } from 'react'
import type { ViewStyle } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { useLockFlowDismiss } from '../flow/FlowDismissLock'
import { useKeyboardHeight } from '../flow/useKeyboardHeight'
import { logger } from '../logging/logger'
import { Box, useTheme } from '../theme/restyle'
import { SwappedWebView } from './SwappedWebView'

/** Props shared by both Swapped embed hosts (deposit and withdrawal). */
export interface SwappedScreenBaseProps {
  /** The selected FOP's `embeddedFlowUrl` to load in the embed. */
  url: string
  /** Close the whole flow (embed asked to exit, e.g. its own ✕). */
  onClose: () => void
  /** Go back to the method list (embed's own back). */
  onBack: () => void
}

interface SwappedScreenShellProps {
  url: string
  lockAmount?: boolean
  onMessage: (msg: SwappedInboundMessage) => void
  /** Android hardware back with no WebView history to pop (see SwappedWebView). */
  onBack: () => void
}

/**
 * Full-screen host frame for the Swapped embedded payment flow
 * ({@link SwappedWebView}). The embed ships its own top bar, so we render no
 * RN header. Mode-agnostic message handling (error and navigation logging)
 * lives here;
 * mode-specific dispatch lives in `SwappedScreen` (deposit) and
 * `SwappedWithdrawScreen` (withdrawal).
 */
export function SwappedScreenShell({
  url,
  onMessage,
  onBack,
  lockAmount = false,
}: SwappedScreenShellProps) {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const keyboardHeight = useKeyboardHeight(true)
  const [step, setStep] = useState<string>()
  // The 3DS / biometric challenge runs inside PAYMENT, not a step of its own.
  // Unmounting the WebView mid-challenge strands the order: Swapped keeps it
  // and we have no resume path, so hold the sheet until the step ends.
  useLockFlowDismiss(step === 'PAYMENT')
  const handleMessage = useCallback(
    (msg: SwappedInboundMessage) => {
      switch (msg.type) {
        case 'SWAPPED_ERROR':
          // Recoverable — the embed surfaces its own error UI (WARN, not ERROR).
          logger.warn('connect-rn:swapped:error', {
            data: msg.data ?? {},
          })
          break
        case 'SWAPPED_READY':
          // Unconditional boot signal. `swapped:theme_sent` also fires on READY
          // but only once the theme send succeeds, so it can't be relied on to
          // count how many times the embed (re)initialised.
          logger.info('connect-rn:swapped:ready')
          break
        case 'SWAPPED_LOGOUT':
          logger.info('connect-rn:swapped:logout')
          break
        case 'SWAPPED_NAVIGATION':
          // Which step the embed moved to. The only signal we get about what
          // it's showing — without it a blank embed is indistinguishable from
          // a user sitting still, which is why this exists (mirrors the web
          // SDK's `swapped-iframe:navigation`).
          logger.info('connect-rn:swapped:navigation', {
            step: msg.data?.step || 'unknown',
          })
          setStep(msg.data?.step)
          break
        default:
          break
      }
      onMessage(msg)
    },
    [onMessage],
  )

  // The sheet sits on the physical screen edge, so the gesture nav bar / home
  // indicator would cover the embed's bottom rows (the Payment breakdown's
  // Network fee), and the WebView can't pad itself: Android reports
  // `env(safe-area-inset-bottom)` as 0. The keyboard already covers that edge
  // when it is up, and FlowSheet lifts the sheet by its height.
  const bottomInset = keyboardHeight > 0 ? 0 : insets.bottom
  const embedHeight = lockAmount ? LOCKED_EMBED_HEIGHT : EMBED_HEIGHT

  return (
    <Box
      borderTopLeftRadius="modal"
      borderTopRightRadius="modal"
      style={[
        sheet,
        {
          height: embedHeight + bottomInset,
          paddingBottom: bottomInset,
          backgroundColor: theme.background.modal,
        },
      ]}
    >
      <SwappedWebView
        url={url}
        onMessage={handleMessage}
        onBack={onBack}
        style={fill}
      />
    </Box>
  )
}

// Fixed embed height (it needs a concrete area; a flex WebView in the
// content-sized bottom sheet would collapse to grey). The shell grows by the
// bottom inset on top of it so the widget's own layout is unchanged. Clipped
// to the rounded top corners.
const EMBED_HEIGHT = 550
// In locked mode, quick options are not shown, so iframe content is smaller
// causing tall blank space with height 550 - reduce to compensate.
const LOCKED_EMBED_HEIGHT = 520
const sheet: ViewStyle = { overflow: 'hidden' }
const fill: ViewStyle = { flex: 1 }
